// utils/printHelper.js

/**
 * Reusable print function for tables with RTL/LTR support
 * @param {Object} config - Configuration object
 * @param {Array} config.data - Data array to print
 * @param {boolean} config.isRTL - Whether to use RTL direction
 * @param {Function} config.t - Translation function
 * @param {string} config.title - Print title
 * @param {Array} config.columns - Column configuration
 * @param {Object} config.styles - Custom styles
 */
export const printTable = (config) => {
  const {
    data = [],
    isRTL = false,
    t = (key) => key,
    title = 'Print',
    columns = [],
    styles = {},
    onAfterPrint,
    onBeforePrint
  } = config;

  const direction = isRTL ? 'rtl' : 'ltr';
  const textAlign = isRTL ? 'right' : 'left';
  
  // Call before print callback if provided
  if (onBeforePrint && typeof onBeforePrint === 'function') {
    onBeforePrint();
  }

  // Create print content
  const printContent = `
    <!DOCTYPE html>
    <html dir="${direction}">
    <head>
      <title>  ${t('Generated by')} ${document.title} </title>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <style>
        /* Base styles */
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: ${isRTL ? "'Segoe UI', 'Arial Arabic', Tahoma" : "'Segoe UI', Arial, sans-serif"};
          direction: ${direction};
          text-align: ${textAlign};
          margin: 20px;
          line-height: 1.6;
          color: #333;
        }
        
        .print-container {
          max-width: 100%;
          margin: 0 auto;
        }
        
        .print-header {
          text-align: center;
          margin-bottom: 30px;
          padding-bottom: 15px;
          border-bottom: 2px solid #2f855a;
        }
        
        .print-title {
          color: #2f855a;
          font-size: 24px;
          margin-bottom: 10px;
        }
        
        .print-meta {
          color: #666;
          font-size: 14px;
          margin-bottom: 5px;
        }
        
        .print-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 20px;
          direction: ${direction};
          font-size: 14px;
        }
        
        .print-table th {
          background-color: #2f855a;
          color: white;
          padding: 12px 15px;
          border: 1px solid #ddd;
          font-weight: bold;
          text-align: ${textAlign};
          vertical-align: middle;
        }
        
        .print-table td {
          padding: 10px 15px;
          border: 1px solid #ddd;
          text-align: ${textAlign};
          vertical-align: middle;
        }
        
        .print-table tr:nth-child(even) {
          background-color: #f8f9fa;
        }
        
        .print-footer {
          margin-top: 30px;
          padding-top: 15px;
          border-top: 1px solid #ddd;
          text-align: center;
          font-size: 12px;
          color: #666;
        }
        
        .no-data {
          text-align: center;
          padding: 40px;
          color: #666;
          font-style: italic;
        }
        
        /* Print specific styles */
        @media print {
          @page {
            margin: 0.5in;
            size: A4 ${isRTL ? 'portrait' : 'portrait'};
          }
          
          body {
            margin: 0;
            font-size: ${isRTL ? '11pt' : '10pt'};
          }
          
          .print-table {
            font-size: ${isRTL ? '10pt' : '9pt'};
          }
          
          .print-title {
            font-size: 18pt;
          }
          
          .no-print {
            display: none !important;
          }
          
          .print-table th {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
        
        /* Custom styles override */
        ${styles.customCSS || ''}
      </style>
    </head>
    <body>
      <div class="print-container">
        <div class="print-header">
          <h1 class="print-title">${title}</h1>
          <div class="print-meta">
            ${new Date().toLocaleDateString()} | ${new Date().toLocaleTimeString()}
          </div>
         
        </div>
        
        ${data.length > 0 ? `
          <table class="print-table">
            <thead>
              <tr>
                ${columns.map(col => `<th>${typeof col.header === 'function' ? col.header(t) : col.header}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${data.map((row, index) => `
                <tr>
                  ${columns.map(col => {
                    const cellContent = typeof col.accessor === 'function' 
                      ? col.accessor(row, index, t)
                      : row[col.accessor];
                    return `<td>${cellContent || 'N/A'}</td>`;
                  }).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        ` : `
          <div class="no-data">
            ${t('No data available for printing')}
          </div>
        `}
        
       
      </div>
      
      <script>
        (function() {
          // Print the document
          window.onload = function() {
            setTimeout(function() {
              window.print();
              
              // Close window after print
              window.onafterprint = function() {
                setTimeout(function() {
                  window.close();
                }, 100);
              };
              
              // Fallback for browsers that don't support onafterprint
              setTimeout(function() {
                window.close();
              }, 1000);
            }, 300);
          };
          
          // Keyboard shortcut to close
          document.addEventListener('keydown', function(e) {
            if (e.key === 'Escape' || (e.ctrlKey && e.key === 'p')) {
              window.close();
            }
          });
        })();
      </script>
    </body>
    </html>
  `;

  // Open print window
  const printWindow = window.open('', '_blank');
  
  if (!printWindow) {
    alert('Please allow popups to print.');
    return;
  }
  
  printWindow.document.write(printContent);
  printWindow.document.close();
  
  // Call after print callback if provided
  printWindow.addEventListener('afterprint', () => {
    if (onAfterPrint && typeof onAfterPrint === 'function') {
      onAfterPrint();
    }
  });
};

/**
 * Simple print function for quick tables
 */
export const simplePrint = ({ data, isRTL, title, headers }) => {
  const direction = isRTL ? 'rtl' : 'ltr';
  
  const tableHTML = `
    <table border="1" style="width:100%; border-collapse:collapse; direction:${direction};">
      <thead>
        <tr>
          ${headers.map(h => `<th style="padding:10px; background:#2f855a; color:white;">${h}</th>`).join('')}
        </tr>
      </thead>
      <tbody>
        ${data.map(row => `
          <tr>
            ${row.map(cell => `<td style="padding:8px;">${cell}</td>`).join('')}
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;
  
  const printWindow = window.open('', '_blank');
  printWindow.document.write(`
    <html>
      <head><title>${title}</title></head>
      <body style="direction:${direction}; margin:20px;">
        <h2>${title}</h2>
        ${tableHTML}
        <script>
          window.onload = () => window.print();
          window.onafterprint = () => window.close();
        </script>
      </body>
    </html>
  `);
  printWindow.document.close();
};